import { logger } from '../core/logger';
import { environment } from '../environments/environment';
import { PaymentComponentConfig, PaymentComponentInstance } from '../types/external/payment-component';
import { PaymentOutputEvent } from '../types/external/payment-output-event';
import { loadMFE } from '../utils/mfe-loader';
import { mountContainer, registerWindowListeners, unmountContainer } from './dom-changes';
import { initializeFraudBootstrap } from './fraud-manager';
import { clearAllSubscriptions, createMessageHandler, initialize, updateLanguage } from './mfe-events';
import { addValidatedEventListener, createEventStore, triggerEvent } from './outside-events';

/**
 * Creates a payment component with methods to mount, update, and unmount
 */
export function configurePaymentComponent(config: PaymentComponentConfig): PaymentComponentInstance {
  const initialState = {
    eventListeners: createEventStore(),
    mounted: false,
    mountedComponent: null as Element | null,
    container: null as HTMLDivElement | null,
    messageHandler: null as ((event: MessageEvent) => void) | null,
    windowListeners: null as { removeListeners: () => void } | null
  };

  let state = { ...initialState };

  const cleanupResources = (): void => {
    if (state.windowListeners) {
      state.windowListeners.removeListeners();
    }

    clearAllSubscriptions();

    unmountContainer(state.container);

    state = { ...initialState };

    logger.info('Payment component resources cleaned up');
  };

  initializeFraudBootstrap();

  return {
    mount: (selector: string) => {
      if (state.mounted) {
        cleanupResources();
      }

      const { mountedComponent, container } = mountContainer(selector);
      if (!mountedComponent || !container) {
        return Promise.reject(new Error(`Failed to mount payment panel to ${selector}`));
      }

      const messageHandler = createMessageHandler((eventName, data) => triggerEvent(state.eventListeners, eventName, data));

      const unloadHandler = () => cleanupResources();

      const windowListeners = registerWindowListeners(messageHandler, unloadHandler);

      state = {
        ...state,
        mounted: true,
        mountedComponent,
        container,
        messageHandler,
        windowListeners
      };

      return loadMFE(environment.microFrontEnds, 'paymentPanel', container)
        .then(() => {
          initialize(config);
        })
        .catch((error) => {
          logger.error(`Failed to load payment panel MFE: ${error}`);
          cleanupResources();
          throw error;
        });
    },

    update: (config: Pick<PaymentComponentConfig, 'language' | 'i18n'>) => {
      updateLanguage(config);
    },

    on: (eventName: PaymentOutputEvent, callback: (data: unknown) => void) => {
      const { store, valid } = addValidatedEventListener(state.eventListeners, eventName, callback);
      if (valid) {
        state = {
          ...state,
          eventListeners: store
        };
      }
    },

    unmount: () => {
      cleanupResources();
    }
  };
}
